import type { JobMaterial } from '@prisma-generated/client'
import { type AttributeConfigModel as FullAttributeConfig } from '@prisma-generated/models/AttributeConfig'
import { type JobOperationsPlanningModel as JobOperationsPlanning } from '@prisma-generated/models/JobOperationsPlanning'
import { type OperationFamilyModel as OperationFamily } from '@prisma-generated/models/OperationFamily'
import { type SheetOperationPlanningModel as SheetOperationPlanning } from '@prisma-generated/models/SheetOperationPlanning'
import { type AttributeConfigFormData } from '@shared/components/AttributeConfigForm'
import { type ReactElement } from 'react'
import { type FieldValues } from 'react-hook-form'

export type Enumerable<T> = T | Array<T>

export type PartialOnlyNullable<T> = {
	[P in keyof T as null extends T[P] ? P : never]?: T[P]
} & {
	[P in keyof T as null extends T[P] ? never : P]: T[P]
}

export type Full<T> = {
	[P in keyof T]-?: NonNullable<T[P]>
}

export type ValueOf<T> = T[keyof T]

export type StringToLiteral<T> = {
	[K in keyof T]: K extends K ? K : never
}

export type Attributes = Record<string, string>

export type FormFieldChoice = { value: string; label: string }

export type AttributeConfig = Pick<FullAttributeConfig, 'kind' | 'label' | 'decimalsToShow' | 'isFirstChoiceDefault'> & {
	attributeName: string
	size: number
	choices?: FormFieldChoice[]
}

export type AttributeColumnAndLabel = Pick<FullAttributeConfig, 'label' | 'columnName'>

type AtLeastOne<T, U = { [K in keyof T]: Pick<T, K> }> = Partial<T> & U[keyof U]
export type FindManyArgs<TWhere, TSelect = unknown, TOrderBy = unknown> = {
	where?: TWhere
	take?: number
	select?: AtLeastOne<TSelect>
	orderBy?: TOrderBy
	page?: number
}

export type OperationConfig = {
	tableName: string
	label: string
	isListType: boolean
	multiLabel: string | null | undefined
	attributes: AttributeConfig[]
	hasYieldCalculation: boolean
	belongsToCurrentSite: boolean
}

export type OperationConfigSimple = Pick<OperationConfig, 'tableName' | 'label'>
export type OperationFamilySimple = Pick<OperationFamily, 'name' | 'isLocked' | 'id'> & { operationTableNames: string[] }

export type CoreSectionConfig = OperationConfig & {
	position: number
	isDisabled: boolean
}

export type SheetSectionAttribute = Attributes & { id?: number }

export type SheetSectionAttributes = {
	simple: SheetSectionAttribute
	multi: SheetSectionAttribute[]
	yield: SheetSectionAttribute[]
}

export type SheetFormSectionDTO = {
	tableName: string
	attributes: SheetSectionAttributes
	label: string
	multiLabel?: string | null
	firstAttributeId?: number | string
	position?: number
}

export interface EntityDTO<T extends FieldValues> {
	id?: number
	name: string
	data: T
}

export interface EntityReference {
	id: number | string
	name: string
	isActive?: boolean
}

export interface WorkstationAffectedGroup {
	id: number
	name: string
	site: { id: number; name: string }
	operation: string
	operationLabel: string
	workstations: Array<{ id: number; name: string }>
}

export type CRUDReturn<T = unknown> = { error: string; field?: string } | { data: T } | null | number | void
export function hasCRUDError(returnValue: CRUDReturn): returnValue is { error: string; field?: string } {
	return !!returnValue && typeof returnValue === 'object' && 'error' in returnValue && returnValue.error.length > 0
}
export function hasCRUDData<T>(returnValue: CRUDReturn): returnValue is { data: T } {
	return !!returnValue && typeof returnValue === 'object' && 'data' in returnValue
}

export type EntityUnknown = EntityReference & Record<string, unknown>

export interface EntityPageProps {
	params: { id: string }
	searchParams?: Record<string, string>
}

export const SatelliteEntity = {
	ENGINEER: 'user',
	MATERIAL_TYPE: 'material_type',
	SITE: 'site',
	WORK_STATION: 'work_station',
} as const
export type SatelliteEntity = ValueOf<typeof SatelliteEntity>

export const AttributeDbType = {
	TEXT: 'text',
	LONG_TEXT: 'long_text',
	NUMBER: 'number',
	DATE: 'date',
	DECIMAL: 'decimal',
} as const
export type AttributeDbType = ValueOf<typeof AttributeDbType>
export function isNumericAttribute(type: AttributeDbType | undefined): boolean {
	if (!type) return false
	return type === AttributeDbType.DECIMAL || type === AttributeDbType.NUMBER
}
export function isTextAttribute(type: AttributeDbType | undefined): boolean {
	if (!type) return false
	return type === AttributeDbType.TEXT || type === AttributeDbType.LONG_TEXT
}

export const AttributeKind = {
	SHORT_TEXT: 'SHORT_TEXT',
	LONG_TEXT: 'LONG_TEXT',
	CHOICE: 'CHOICE',
	NUMBER: 'NUMBER',
	BOOLEAN: 'BOOLEAN',
	SIGNATURE: 'SIGNATURE',
} as const
export type AttributeKind = ValueOf<typeof AttributeKind>
export const kindToDbTypeMap = new Map<AttributeKind, AttributeDbType>([
	[AttributeKind.BOOLEAN, AttributeDbType.TEXT],
	[AttributeKind.CHOICE, AttributeDbType.LONG_TEXT],
	[AttributeKind.LONG_TEXT, AttributeDbType.LONG_TEXT],
	[AttributeKind.NUMBER, AttributeDbType.DECIMAL],
	[AttributeKind.SHORT_TEXT, AttributeDbType.TEXT],
	[AttributeKind.SIGNATURE, AttributeDbType.TEXT],
])

export const UserRole = {
	USER: 'USER',
	ADMIN: 'ADMIN',
	GUEST: 'GUEST',
	SITE_MANAGER: 'SITE_MANAGER',
} as const
export type UserRole = ValueOf<typeof UserRole>

export type UserSiteAccess = {
	siteId: number | string
	site?: EntityReference
	jmtAccess: boolean
	qmtAccess: boolean
}

export type SearchParams = Record<string, string>
export const ListPageQueryFilters = {
	orderBy: 'orderBy',
	orderDirection: 'orderDirection',
	search: 'search',
	page: 'page',
} as const

export interface AttributeDTO {
	name: string
	isMultiField: boolean
	isYieldCalculation: boolean
	configuration: Omit<AttributeConfigFormData, 'columnName' | 'position'>
	isWorkstation?: boolean
	isMaterial?: boolean
	hasUnit?: boolean
}
export type CreateAttributeFormData = AttributeDTO & { position: number; addAtEnd: boolean }

export const RecordType = {
	SIMPLE: 'SIMPLE',
	MULTI: 'MULTI',
	YIELD: 'YIELD',
} as const
export type RecordType = ValueOf<typeof RecordType>

export const OperationStatus = {
	COMPLETED: 'COMPLETED',
	INCOMPLETE: 'INCOMPLETE',
	NOT_REQUIRED: 'NOT_REQUIRED',
} as const
export type OperationStatus = ValueOf<typeof OperationStatus>

export type SheetOperationPlanningDTO = Pick<SheetOperationPlanning, 'estimateTime' | 'operationName' | 'setupTime' | 'offSet'> & {
	status: OperationStatus
	id?: number
	firstOperationAttributeId?: number
	jobOperationPlanningId?: number
	possibleShifts?: number[] | null
	possibleWorkstations?: EntityReference[] | null
	routeOwners?: RouteOwnerDTO[] | null
	startDate?: Date | null
	completedDate?: Date | null
	label?: string
	description?: string | null
	nextOperationExecution?: OperationExecutionSuggestion
	estimatedTimeRemaining: number | null
	remainingRunFraction?: number | null
}

type MaterialDTO = Pick<JobMaterial, 'arrivalDate' | 'estimatedArrivalDate' | 'recommitDate'>

export type RouteOwnerDTO = {
	shift: number
	user?: EntityReference | null
	userId?: number | null
	userNotInSystem?: string | null
}

export type JobOperationPlanningDTO = Pick<JobOperationsPlanning, 'estimateTime' | 'operationName' | 'setupTime' | 'offSet'> & {
	status: OperationStatus
	id?: number
	firstOperationAttributeId?: number
	possibleShifts: number[] | null
	possibleWorkstations: EntityReference[] | null
	routeOwners?: RouteOwnerDTO[] | null
	startDate?: Date | null
	completedDate?: Date | null
	description?: string | null
	nextOperationExecution?: OperationExecutionSuggestion | null
	estimatedTimeRemaining: number | null
	materials: MaterialDTO[]
	remainingRunFraction?: number | null
}

export const JobStatus = {
	OPEN: 'OPEN',
	CLOSED: 'CLOSED',
} as const
export type JobStatus = ValueOf<typeof JobStatus>

export const JobWorkflowStatus = {
	PLANNING: 'PLANNING',
	WAITING_ON_MATERIALS: 'WAITING_ON_MATERIALS',
	PROCESSING: 'PROCESSING',
	OUTSOURCED: 'OUTSOURCED',
	SHIPPING: 'SHIPPING',
	PLANNING_NOT_REQUIRED: 'PLANNING_NOT_REQUIRED',
	COMPLETED: 'COMPLETED',
} as const
export type JobWorkflowStatus = ValueOf<typeof JobWorkflowStatus>

export const CustomerReportStatus = {
	PRINTED: 'printed',
	READY_TO_SEND: 'readyToSend',
	SENT: 'sent',
	NOT_SENT: 'notSent',
} as const
export type CustomerReportStatus = ValueOf<typeof CustomerReportStatus>

export const FeatureAnalyticType = {
	BLANK_PPT: 'Blank_PPT_Created',
	HOME_PAGE_CREATE_PPT: 'Home_Page_Create_PPT',
	CUSTOMER_REPORT: 'Customer_Report',
	VELOCITY_TAG: 'Velocity_Tag',
	PRINT_PDF: 'Print_PDF',
	PRINT_COC: 'Print_COC',
	DUPLICATE_PPT: 'Duplicate_Sheet',
	EDIT_TEMPLATE: 'Edit_Template',
	EDIT_OPERATION_ATTRIBUTE: 'Edit_Operation_Attribute',
	PPT_MANUAL_COMPLETION: 'Sheet_Manual_Completion',
	SIMILAR_PPT_VIEW: 'Similar_Sheet_View',
	SIMILAR_PPT_PLANNING: 'Similar_Sheet_Planning',
	ARCHIVE_PPT: 'Archive_PPT',
	VERSION_HISTORY: 'Version_History',
	FILTER_SITE: 'Filter_Site',
	FILTER_CUSTOMER: 'Filter_Customer',
	FILTER_MATERIAL: 'Filter_Material',
	FILTER_OPERATION: 'Filter_Operation',
	FILTER_OPERATION_ATTRIBUTE: 'Filter_Operation_Attribute',
	FILTER_RECIPE: 'Filter_Recipe',
	FILTER_DUE_DATE: 'Filter_DueDate',
	MOVE_OPERATION_ATTRIBUTE: 'Move_Operation_Attribute',
	SEND_FEEDBACK: 'Send_Feedback',
	SUBMIT_FEEDBACK: 'Submit_Feedback',
	READ_FEEDBACK: 'Read_Feedback',
	SOLVED_FEEDBACK: 'Solved_Feedback',
	ARCHIVE_FEEDBACK: 'Archive_Feedback',
	HOME_PAGE_CREATE_JOB: 'Home_Page_Create_JOB',
} as const
export type FeatureAnalyticType = ValueOf<typeof FeatureAnalyticType>

export const SuggestionStatus = {
	RESOLVED: 'RESOLVED',
	UNRESOLVED: 'UNRESOLVED',
	ARCHIVED: 'ARCHIVED',
} as const
export type SuggestionStatus = ValueOf<typeof SuggestionStatus>

export interface SortModel {
	field: string
	sort: 'asc' | 'desc' | undefined | null
}

export const PLANNING_OPERATIONS = 'operations'
export const OPERATIONS_PLANNING = 'operationsPlanning'

export const FileUploadStatus = {
	UPLOADING: 'UPLOADING',
	FAILED: 'FAILED',
	FAILED_TO_DELETE: 'FAILED_TO_DELETE',
} as const
export type FileUploadStatus = ValueOf<typeof FileUploadStatus>

export const OperationExecutionSuggestion = {
	SEQUENTIALLY: 'SEQUENTIALLY',
	OFFSET: 'OFFSET',
} as const
export type OperationExecutionSuggestion = ValueOf<typeof OperationExecutionSuggestion>

export type OperationDragAndDropObject = {
	id?: number
	operationName: string
	label: string
	description?: string | null
	estimateTime?: number | null
	setupTime?: number | null
	possibleWorkstations?: EntityReference[]
	possibleShifts?: number[]
	nextOperationExecution?: OperationExecutionSuggestion | null
	offSet?: number | null
	routeOwners?: RouteOwnerDTO[]
}

export type RenderedCell = {
	cell: ReactElement
	size: number
	label: string
}

export type OpportunitySummaryDTO = {
	id: number
	site?: string
	customer: string
	project: string
	quoteDescription: string
	quoteNumber?: string
	productGroup: string
	bdm: string
	user?: string
	rfqDate: string | null
	quoteType: string
}
